/**
 * R-16 T-135 (v1.2 change 02) — the pure half of the kit Menu: hover intent for a bar of menus, and the mega
 * menu's columns. No React, no DOM; the timers come from a clock so the rules are tested without a browser.
 *
 *   open    after the pointer rests on a trigger for `open` ms (--menu-open-delay, 150)
 *   switch  once one menu of the bar is open, entering a neighbour opens it at once
 *   close   `grace` ms after the pointer leaves both trigger and popup (--menu-close-grace, 280)
 *   pin     a press (click, tap, Enter/Space/↓) opens and pins; pinned menus ignore the pointer leaving;
 *           a second press on the same trigger closes; an item, an outside click or Esc dismisses
 *   one     one menu of the bar is open at a time
 */
export interface Clock {
  set(fn: () => void, ms: number): unknown;
  clear(handle: unknown): void;
}

export const browserClock: Clock = { set: (fn, ms) => setTimeout(fn, ms), clear: (h) => clearTimeout(h as ReturnType<typeof setTimeout>) };

export class MenuIntent {
  open: string | null = null;
  pinned = false;
  private openTimer: unknown = null;
  private closeTimer: unknown = null;
  private listeners = new Set<() => void>();

  constructor(
    private readonly delays: () => { open: number; grace: number } = () => ({ open: 150, grace: 280 }),
    private readonly clock: Clock = browserClock,
  ) {}

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private set(open: string | null, pinned: boolean) {
    const changed = open !== this.open || pinned !== this.pinned;
    this.open = open;
    this.pinned = open !== null && pinned;
    if (changed) for (const fn of this.listeners) fn();
  }

  private stop() {
    if (this.openTimer !== null) this.clock.clear(this.openTimer);
    if (this.closeTimer !== null) this.clock.clear(this.closeTimer);
    this.openTimer = this.closeTimer = null;
  }

  /** The pointer entered menu `id`'s trigger or popup. */
  enter(id: string) {
    if (this.closeTimer !== null) this.clock.clear(this.closeTimer);
    this.closeTimer = null;
    if (this.open === id) return;
    if (this.open !== null) {
      this.stop();
      this.set(id, false);
      return;
    }
    if (this.openTimer !== null) this.clock.clear(this.openTimer);
    this.openTimer = this.clock.set(() => {
      this.openTimer = null;
      this.set(id, false);
    }, this.delays().open);
  }

  /** The pointer left menu `id`'s trigger or popup. */
  leave(_id: string) {
    if (this.openTimer !== null) this.clock.clear(this.openTimer);
    this.openTimer = null;
    if (this.open === null || this.pinned) return;
    if (this.closeTimer !== null) this.clock.clear(this.closeTimer);
    this.closeTimer = this.clock.set(() => {
      this.closeTimer = null;
      this.set(null, false);
    }, this.delays().grace);
  }

  /** A click, tap or key on menu `id`'s trigger. */
  press(id: string) {
    this.stop();
    if (this.open !== id) this.set(id, true);
    else if (!this.pinned) this.set(id, true);
    else this.set(null, false);
  }

  /** Esc, an outside click, an item picked, focus gone. */
  dismiss() {
    this.stop();
    this.set(null, false);
  }
}

/** One column of a mega menu: the group's title (null = the untitled column) and its items in declared order. */
export interface MenuColumn<T> {
  title: string | null;
  items: T[];
}

/** Up to this many items a menu is a plain list with no titles. */
export const PLAIN_LIST_MAX = 4;
export const MAX_COLUMNS = 4;

/**
 * Items with the same `group` form a column titled by it; items with none share one untitled column. Columns follow
 * the order of each group's first item. Up to PLAIN_LIST_MAX items: one untitled column (a plain list).
 */
export function menuColumns<T extends { group?: string | null }>(items: readonly T[]): MenuColumn<T>[] {
  if (items.length <= PLAIN_LIST_MAX) return items.length ? [{ title: null, items: [...items] }] : [];
  const cols = new Map<string, MenuColumn<T>>();
  for (const it of items) {
    const key = it.group?.trim() || '';
    const col = cols.get(key) ?? { title: key || null, items: [] };
    col.items.push(it);
    cols.set(key, col);
  }
  return [...cols.values()];
}

/** A CSS time token (`150ms`, `0.28s`) in milliseconds; `fallback` when unreadable. */
export function cssMs(value: string | null | undefined, fallback: number): number {
  const v = value?.trim() ?? '';
  const n = Number.parseFloat(v);
  if (!Number.isFinite(n)) return fallback;
  return v.endsWith('ms') ? n : v.endsWith('s') ? n * 1000 : n;
}
